import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ThemeColors, ButtonConfig } from './theme.service';

@Injectable({
  providedIn: 'root'
})
export class ThemeRepository {
  private readonly host = environment.apiUrlHost;

  constructor(private http: HttpClient) {}

  /**
   * Get active theme configuration from backend
   */
  getActiveThemeConfig(): Observable<any> {
    console.log('🔍 ThemeRepository: Getting active theme from /AD/themes/active/');
    return this.http.get<any>(`${this.host}/AD/themes/active/`).pipe(
      tap(response => {
        console.log('🔍 ThemeRepository: Raw response from /AD/themes/active/:', response);
        console.log('🔍 ThemeRepository: Response type:', typeof response);
        console.log('🔍 ThemeRepository: Response keys:', Object.keys(response || {}));
      })
    );
  }

  /**
   * Get all theme configurations from backend
   */
  getThemeConfig(): Observable<any> {
    return this.http.get<any>(`${this.host}/AD/themes/`);
  }

  /**
   * Get specific theme by ID
   */
  getThemeById(id: number): Observable<any> {
    return this.http.get<any>(`${this.host}/AD/themes/${id}/`);
  }

  /**
   * Save color palette to backend (creates new theme)
   */
  saveColorPalette(colors: ThemeColors): Observable<any> {
    console.log('🔄 ThemeRepository: POST /AD/themes/ (creating new theme) with colors:', colors);
    return this.http.post<any>(`${this.host}/AD/themes/`, colors);
  }

  /**
   * Update existing theme configuration
   */
  updateThemeConfig(id: number, colors: ThemeColors): Observable<any> {
    console.log(`🔄 ThemeRepository: PUT /AD/themes/${id}/ with colors:`, colors);
    return this.http.put<any>(`${this.host}/AD/themes/${id}/`, colors);
  }

  /**
   * Get button configurations from backend
   */
  getButtonConfigs(): Observable<any> {
    return this.http.get<any>(`${this.host}/AD/button-configs/`);
  }

  /**
   * Get button configs for specific theme
   */
  getButtonConfigsByTheme(themeId: number): Observable<any> {
    return this.http.get<any>(`${this.host}/AD/button-configs/?theme=${themeId}`);
  }

  /**
   * Get button configurations for active theme
   */
  getActiveButtonConfigs(): Observable<any> {
    return this.getActiveThemeConfig().pipe(
      switchMap(activeTheme => {
        if (activeTheme && activeTheme.id) {
          return this.getButtonConfigsByTheme(activeTheme.id);
        } else {
          return this.getButtonConfigs();
        }
      })
    );
  }

  /**
   * Update color palette (always uses PUT to update existing theme)
   */
  updateColorPalette(colors: ThemeColors): Observable<any> {
    console.log('🔄 ThemeRepository: updateColorPalette called with colors:', colors);

    // Get the active theme first
    return this.getActiveThemeConfig().pipe(
      switchMap(activeTheme => {
        console.log('🔍 ThemeRepository: Active theme found:', activeTheme);

        // Handle different response structures
        let themeId = null;

        if (activeTheme) {
          // Check if response has id field directly
          if (activeTheme.id) {
            themeId = activeTheme.id;
          }
          // Check if response is wrapped in another object
          else if (activeTheme.data && activeTheme.data.id) {
            themeId = activeTheme.data.id;
          }
          // Check if response is an array with first element
          else if (Array.isArray(activeTheme) && activeTheme.length > 0 && activeTheme[0].id) {
            themeId = activeTheme[0].id;
          }
        }

        if (themeId) {
          console.log(`✅ ThemeRepository: Updating existing theme ${themeId} with PUT`);
          return this.updateThemeConfig(themeId, colors);
        } else {
          console.log('ℹ️ ThemeRepository: No active theme found - creating a new active theme with POST');
          // Create a new theme as active when none exists
          const createPayload: any = { ...colors, is_active: true } as any;
          return this.saveColorPalette(createPayload);
        }
      })
    );
  }

  /**
   * Save button configuration to backend
   */
  saveButtonConfig(buttonConfig: ButtonConfig): Observable<any> {
    return this.http.post<any>(`${this.host}/AD/button-configs/`, buttonConfig);
  }

  /**
   * Update existing button configuration
   */
  updateButtonConfig(id: number, buttonConfig: ButtonConfig): Observable<any> {
    console.log(`🔄 ThemeRepository: PUT /AD/button-configs/${id}/ with config:`, buttonConfig);
    return this.http.put<any>(`${this.host}/AD/button-configs/${id}/`, buttonConfig);
  }

  /**
   * Update multiple button configurations (PUT only)
   */
  saveButtonConfigs(buttonConfigs: ButtonConfig[]): Observable<any> {
    console.log('🔘 ThemeRepository: saveButtonConfigs called with:', buttonConfigs);

    if (buttonConfigs.length === 0) {
      console.log('ℹ️ ThemeRepository: No button configs to update');
      return new Observable(observer => {
        observer.next([]);
        observer.complete();
      });
    }

    // Get existing button configs to find their IDs
    return this.getButtonConfigs().pipe(
      switchMap(existingConfigs => {
        console.log('🔍 ThemeRepository: Existing button configs:', existingConfigs);

        const updateObservables = buttonConfigs.map(config => {
          // Find existing config by button_type
          const existingConfig = existingConfigs.find(existing =>
            existing.button_type === config.button_type
          );

          if (existingConfig && existingConfig.id) {
            console.log(`✅ ThemeRepository: Updating existing button config ${existingConfig.id} (${config.button_type}) with PUT`);
            return this.updateButtonConfig(existingConfig.id, config);
          } else {
            console.log(`ℹ️ ThemeRepository: No existing button config found for ${config.button_type} - creating new with POST`);
            // Try to associate with active theme if possible
            return this.getActiveThemeConfig().pipe(
              switchMap(activeTheme => {
                const themeId = activeTheme?.id || activeTheme?.data?.id || (Array.isArray(activeTheme) && activeTheme[0]?.id) || null;
                const payload: any = themeId ? { ...config, theme: themeId } : config;
                return this.saveButtonConfig(payload);
              })
            );
          }
        });

        // Execute all updates
        return new Observable(observer => {
          let completed = 0;
          const total = updateObservables.length;
          const results = [];

          updateObservables.forEach((updateObs, index) => {
            updateObs.subscribe({
              next: (response) => {
                results[index] = response;
                completed++;
                if (completed === total) {
                  observer.next(results.filter(r => r !== null));
                  observer.complete();
                }
              },
              error: (error) => {
                console.error(`❌ ThemeRepository: Error updating button config ${index}:`, error);
                observer.error(error);
              }
            });
          });
        });
      })
    );
  }
}

